import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { appConfig } from '../config/app.config';
import { LoaderService } from '../util/loader.service';
import { ToastService } from '../util/toast.service';
import { UtilityTs } from '../util/utilityTs';
import { AuthService } from './auth.service';
import { LocalDbService } from './localDb.service';

@Injectable({
  providedIn: 'root',
})
export class BackEndService {
  private static readonly SESSION_STORAGE_KEY = 'session';
  private static readonly JWT_STORAGE_KEY = 'jwt';

  private static isHandlingAuthError = false;

  rowPerPage = 200;
  currentPage = 0;
  totalCount = 0;
  pageArray = [];
  baseUrl = environment.apiUrl + '/api/vanshavali/data';

  private sessionSubject = new BehaviorSubject<any | null>(null);
  session$ = this.sessionSubject.asObservable();
  session: any | null = null;
  jwt: string | null = null;
  private getSessionPromise: Promise<void> | null = null;

  constructor(
    private toast: ToastService,
    private loader: LoaderService,
    private http: HttpClient,
    private localDb: LocalDbService,
    private injector: Injector

  ) {
    // Fire-and-forget ping to wake up the server (Render free tier spin-up)
    this.wakeUpServer();
  }

  private wakeUpServer() {
    this.http.get(environment.apiUrl + '/api/ping').subscribe({
      next: () => console.log('🚀 Server is awake'),
      error: () => console.warn('⚠️ Server wake-up ping failed (might be normal on cold starts)')
    });
  }

  getUser() {
    if (!this.session) return null;
    return this.session.user ??
      this.session.session?.user ??
      this.session.data?.user ??
      (this.session.email || this.session.id ? this.session : null);
  }

  async setSession(payload: any | null): Promise<void> {

    if (!payload) {
      this.session = null;
      this.jwt = null;
      this.sessionSubject.next(null);
      await this.localDb.localDelete(BackEndService.SESSION_STORAGE_KEY);
      await this.localDb.localDelete(BackEndService.JWT_STORAGE_KEY);
      return;
    }
    this.session = payload;
    // Deep search for JWT in various common response structures
    this.jwt = payload?.jwt
      ?? payload?.data?.jwt
      ?? payload?.session?.access_token
      ?? payload?.data?.session?.access_token
      ?? payload?.access_token;


    this.sessionSubject.next(this.session);
    if (this.session) {
      await this.localDb.localPost(BackEndService.SESSION_STORAGE_KEY, this.session);
      if (this.jwt) {
        await this.localDb.localPost(BackEndService.JWT_STORAGE_KEY, this.jwt);
      }
    }
  }

  isJwtExpired(token: string): boolean {
    if (!token) return true;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return true;
      const payload = JSON.parse(atob(parts[1]));
      const expirationDate = payload.exp * 1000;
      // Add a 30s buffer to prevent edge-case failures during request transit
      return Date.now() > (expirationDate - 30000);
    } catch {
      return true;
    }
  }

  async getSession(force: boolean = false) {
    if (this.getSessionPromise && !force) {
      return this.getSessionPromise;
    }

    this.getSessionPromise = (async () => {
      let spinUpTimer: any;
      try {
        const localSession = await this.localDb.localGet(BackEndService.SESSION_STORAGE_KEY);
        const localJwt = await this.localDb.localGet(BackEndService.JWT_STORAGE_KEY);

        if (!force && localSession && localJwt && !this.isJwtExpired(localJwt)) {
          this.session = localSession;
          this.jwt = localJwt;
          this.sessionSubject.next(this.session);
          return;
        }

        // Inform user if server takes too long (spin-up detection)
        spinUpTimer = setTimeout(() => {
          this.toast.warning('Please wait a moment...', 5000);
        }, 5000);

        const response: any = await firstValueFrom(
          this.http.post(environment.apiUrl + '/api/auth/session', {}, { withCredentials: true })
        );

        if (response) {
          await this.setSession(response);
          this.jwt = this.jwt ?? response.jwt ?? response.access_token;
        } else if (force || this.isJwtExpired(localJwt)) {
          this.session = null;
          this.jwt = null;
          this.sessionSubject.next(null);
        }
      } catch (httpError: any) {
        if (!environment.production) {
          console.error("BackEndService: Error in getSession:", httpError);
        }
      } finally {
        if (spinUpTimer) clearTimeout(spinUpTimer);
        this.getSessionPromise = null;
      }
    })();

    return this.getSessionPromise;
  }


  deleteObject(tableName, value, variable?) {
    return new Promise(async (resolve) => {
      console.log("Deleting tableName: " + tableName + " : " + value + " : " + variable);
      const response = await this.apiCall(tableName, 'delete', { whereList: [[variable ?? 'id', value]] });
      const { data, error } = response;
      console.log("Deleted: ", data, error);
      if (error) {
        this.loader.hideForce();
        console.error("Deleting tableName: " + tableName + " : " + value + " : " + variable, error);

        const message = error.message;
        if (
          response.status === 401 ||
          response.status === 403 ||
          message === 'JWT expired' ||
          (message && message.includes('row-level security policy'))
        ) {
          console.warn('Auth/Session error in deleteObject, logging out...', message);
          const authService = this.injector.get(AuthService);
          authService.signOut(false, false, true); // silent auto-logout
        } else {
          this.toast.error("Error deleting record: " + message);
        }
        resolve(null)
      } else {
        this.loader.hideForce();
        resolve(true);
      }
    })
  }

  async getObjectById(tableName, id, otherTable?) {
    return new Promise((resolve) => {
      this.getSingle(tableName, [["id", id]], otherTable).then((response) => {
        this.loader.hideForce();
        resolve(response);
      });
    });
  }

  async getSingle(tableName, whereList: any[][], otherTables?: string, select?: string) {
    const results: any = await this.searchObject(tableName, whereList, 0, 1, undefined, undefined, otherTables, !!select);
    this.loader.hideForce();
    if (results && results.length > 0) {
      return results[0];
    }
    return null;
  }


  private isAuthError(status: number, statusText: string, message: string, errCode: string): boolean {
    return (
      status === 401 ||
      statusText === 'Unauthorized' ||
      errCode === 'SESSION_EXPIRED' ||
      message === 'JWT expired' ||
      message === 'Not authenticated' ||
      (message && (
        message.includes('Session expired') ||
        message.includes('jwt expired') ||
        message.includes('not authenticated')
      ))
    );
  }

  async apiCall(tableName: string, action: string, params: any, skipToken: boolean = false): Promise<any> {
    const normalizedParams = { ...(params ?? {}) };
    if (normalizedParams.where === undefined && Array.isArray(normalizedParams.whereList)) {
      normalizedParams.where = (normalizedParams.whereList as any[][]).map((clause) => {
        if (Array.isArray(clause) && clause.length === 2) {
          return [clause[0], clause[1], 'eq'];
        }
        return clause;
      });
      delete normalizedParams.whereList;
    }

    if (normalizedParams.ascending === undefined && (normalizedParams.reverse !== undefined || normalizedParams.orderBy !== undefined)) {
      normalizedParams.ascending = !(normalizedParams.reverse ?? true);
      delete normalizedParams.reverse;
    }

    const body: any = { action, table: tableName, ...normalizedParams, };
    if (action === 'rpc') {
      body.tableName = tableName;
      body.function = tableName;
      body.function_name = tableName;
    }

    const getToken = () => this.jwt ?? this.session?.access_token
      ?? this.session?.jwt
      ?? this.session?.session?.access_token;

    const makeCall = async () => {
      const headers: any = {};
      const token = getToken();
      if (token && !skipToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      return await firstValueFrom(this.http.post(this.baseUrl, body, { withCredentials: !skipToken, headers, observe: 'response' })) as any;
    };

    try {
      let response = await makeCall();
      const statusText = response?.statusText ?? '';
      const message = response?.body?.error?.message ?? '';
      const errCode = response?.body?.error?.code ?? response?.body?.error?.error ?? '';

      if (this.isAuthError(response.status, statusText, message, errCode)) {
        console.warn('Auth/Session stale in apiCall, attempting one-time refresh...');
        await this.getSession(true);
        if (getToken()) {
          console.log('Refresh successful, retrying original request:', action, tableName);
          response = await makeCall();
        }
      }
      return response;
    } catch (httpError: any) {
      console.error("❌ Exception during apiCall:", httpError);
      return httpError;
    }
  }
  async forceClearAll() {
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;");
    });
    localStorage.clear();
    sessionStorage.clear();
  }



  private extractParamFromQuery(url: string, param: string): string | null {
    try {
      const query = url.split('?')[1] ?? '';
      const params = new URLSearchParams(query);
      return params.get(param);
    } catch {
      return null;
    }
  }

  private toPlainObject<T>(value: T): T {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      if (!environment.production) {
        console.warn('Failed to serialize object, sending as-is:', error);
      }
      return value;
    }
  }

  ping(url): Promise<any> {
    return firstValueFrom(this.http.get(url, { withCredentials: true }));
  }

  async countObject(tableName: string, whereList: any[][], base: string) {
    let response: any = await this.apiCall(tableName, 'select', { whereList, select: base });
    const abc = this.returnResponse(tableName, response);
    const stateCount = abc.reduce((acc: { [key: string]: number }, record) => {
      acc[record[base]] = (acc[record[base]] || 0) + 1;
      return acc;
    }, {});
    return stateCount;
  }

  async searchObject(
    tableName,
    whereList?: any[][],
    index?: number,
    limit?: number,
    orderBy?: string,
    reverse?: boolean,
    otherTables?: string,
    removeStar?: boolean,
    range?: { from: number, to: number },
    skipToken: boolean = false
  ) {
    this.loader.show();
    let select = "*";
    if (removeStar) select = "";
    if (otherTables) select += "," + otherTables;

    const where = whereList?.map((clause) => {
      if (Array.isArray(clause) && clause.length === 2) {
        return [clause[0], clause[1], 'eq'];
      }
      return clause;
    });

    const ascending = !(reverse ?? true);
    const payload: any = { where, select };

    if (orderBy) {
      payload.orderBy = orderBy;
      payload.ascending = ascending;
    }
    if (index !== undefined) payload.index = index;
    if (limit !== undefined) payload.limit = limit;
    if (range) payload.range = range;

    let response: any = await this.apiCall(tableName, 'search', payload, skipToken);
    this.loader.hideForce();
    return this.returnResponse(tableName, response, limit);
  }

  returnResponse(tableName, response, limit?) {
    if (response.status < 300) {
      if (response.body.count) {
        this.totalCount = response.body.count;
        if (this.totalCount > 0) {
          let floor = Math.floor(this.totalCount / (limit ?? 20));
          this.pageArray = [...Array(Math.min(floor, 10)).keys()].map(i => i + 1); // simplified for now
        }
      }
      this.loader.hideForce();
      return response.body.data;
    } else {
      this.loader.hideForce();
      const details = response?.error?.details;
      const message = response?.error?.message;

      // Use centralized auth error check
      const errCode = response?.error?.code ?? response?.error?.error ?? '';
      const statusText = response?.statusText ?? '';
      if (this.isAuthError(response.status, statusText, message ?? '', errCode)) {
        if (BackEndService.isHandlingAuthError) {
          console.warn(' Auth error already in progress, skipping signOut in returnResponse');
          return null;
        }
        console.warn(' Auth/Session error detected, auto-logout...', errCode || message);
        BackEndService.isHandlingAuthError = true; // 🔒 Set guard
        const authService = this.injector.get(AuthService);
        authService.signOut(false, false, true);
        return null;
      }

      if (details !== 'The result contains 0 rows') {
        if (message) {
          this.toast.error(message);
        }
        if (!environment.production) {
          console.log(response);
        }
      }
      if (limit) {
        if (!environment.production) {
          console.log(tableName + " Response: No data");
        }
        return [];
      } else {
        if (!environment.production) {
          console.log(tableName + " Response: Null data");
        }
        return null;
      }
    }
  }

  async saveObject(tableName, object) {
    if (!object || typeof object !== 'object') {
      this.toast.error('Invalid data. Please try again.');
      return null;
    }
    if (tableName != "user_whatsapp") {
      object.id = object.id ?? UtilityTs.generateId();
    }
    object.created_at = object.created_at ?? new Date();
    object.active = object.active ?? true;
    const update = { ...object, updated_at: new Date() };
    const payload = this.toPlainObject(update);
    this.deleteObjectsWhileSaving(tableName, payload);

    if (Object.keys(payload).length === 0) return null;

    let response: any = await this.apiCall(tableName, 'upsert', { payload: payload });
    this.loader.hideForce();
    if (response.status < 300) {
      return payload;
    } else {
      const message = response.error?.message || '';
      const errCode = response.error?.code ?? response.error?.error ?? '';
      if (
        response.status === 401 ||
        response.status === 403 ||
        errCode === 'SESSION_EXPIRED' ||
        message === 'JWT expired' ||
        message.includes('Session expired') ||
        message.includes('row-level security policy')
      ) {
        console.warn('⚠️ Session expired / auth error in saveObject, logging out...');
        const authService = this.injector.get(AuthService);
        authService.signOut(false, false, true); // silent auto-logout
      } else {
        this.toast.error(message || 'Save failed');
      }
      return null;
    }
  }

  deleteObjectsWhileSaving(tableName, update) {
    delete update.pageValidation;
    delete update.local;
    if (tableName == appConfig.STORAGE_TABLE.INVITE) {
      delete update.employee;
    }
    if (tableName == "request") {
      delete update.user;
    }
    if (tableName == "user") {
      delete update.business_id;
      delete update.user_pd;
    }
    if (tableName == "election") {
      delete update.member_list;
      delete update.candidate_list;
      delete update.coordinator_list;
    }
    if (tableName == "account_deletion_requests") {
      delete update.active;
      delete update.business_id;
    }
    if (tableName == "user_pd") {
      if (update.complete_address && !update.current_address2) {
        update.current_address2 = update.complete_address;
      }
      delete update.complete_address
    }
    if (tableName == "role" || tableName == "user_whatsapp" || tableName == "business_request" || tableName == "app_version") {
      delete update.business_id;
    }
    if (tableName == "employee") {
      delete update.user;
      delete update.creator;
    }
    if (tableName == "relative") {
      delete update.existing;
    }

  }

  async updateList(tableName, objectList) {
    let objList = [];
    for (let obj of objectList) {
      obj.id = obj.id ?? UtilityTs.generateId();
      obj.created_at = obj.created_at ?? new Date();
      obj.active = obj.active ?? true;
      let update = {
        ...obj,
        updated_at: new Date(),
      }
      const payload = this.toPlainObject(update);
      this.deleteObjectsWhileSaving(tableName, payload);
      if (Object.keys(payload).length > 0) {
        objList.push(payload);
      }
    }
    let response: any = await this.apiCall(tableName, 'upsert', { payload: objList });

    if (response.status == 200) {
      return 200;
    } else {
      const message = response?.error?.message;
      if (
        response.status === 401 ||
        response.status === 403 ||
        message === 'JWT expired' ||
        (message && message.includes('row-level security policy'))
      ) {
        console.warn('Auth/Session error in updateList, logging out...', message);
        const authService = this.injector.get(AuthService);
        authService.signOut(false, false, true); // silent auto-logout
      } else if (message) {
        this.toast.error(message);
      }
      this.loader.hideForce();
      return null;
    }
  }

  create(tableName, object) {
    const update = {
      ...object,
      updated_at: new Date(),
      created_at: new Date(),
      active: true,
      id: UtilityTs.generateId(),
    };
    delete update.pageValidation;
    delete update.local;
    return this.apiCall(tableName, 'insert', { payload: update });
  }

  deleteList(tableName, objectList: any[]) {
    return new Promise((resolve) => {
      const arrayList = objectList.map(item => item.id);
      this.apiCall(tableName, 'delete', { whereList: [['id', arrayList, 'in']] }).then(() => {
        resolve(true);
      });
    });
  }


  async signInWithGoogleFirebase(firebaseIdToken: string): Promise<any> {
    try {
      const response: any = await firstValueFrom(
        this.http.post(
          environment.apiUrl + '/api/auth/verify-token-generate-session',
          { firebaseIdToken, domain: 'vanshavali', provider: 'google' },
          { withCredentials: true }
        )
      );
      console.log('Google Login Response', response);
      if (response) {
        await this.setSession(response);
      }
      return response;
    } catch (httpError: any) {
      const errBody = httpError?.error ?? {};
      const status = httpError?.status ?? 500;
      const message = errBody?.message ?? httpError?.message ?? 'Google login failed';
      const errCode = errBody?.code ?? errBody?.error ?? '';

      console.error('❌ Google Login Error:', status, errCode, message);

      if (this.isAuthError(status, httpError?.statusText ?? '', message, errCode)) {
        console.warn('⚠️ Stale session detected during Google login, clearing state...');
        // Only clear state if we're not already handling it
        if (!BackEndService.isHandlingAuthError) {
          BackEndService.isHandlingAuthError = true;
          await this.signOut();
          BackEndService.isHandlingAuthError = false;
        }
      }
      throw { message, status, code: errCode };
    }
  }

  async signInWithOtp(firebaseIdToken: string, provider?: string, metadata?: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(
        this.http.post(
          environment.apiUrl + '/api/auth/verify-token-generate-session',
          { firebaseIdToken, domain: 'vanshavali', provider, ...metadata },
          { withCredentials: true }
        )
      );

      console.log('OTP Login Response', response);
      if (response) {
        await this.setSession(response);
      }
      return response;
    } catch (httpError: any) {
      const errBody = httpError?.error ?? {};
      const status = httpError?.status ?? 500;
      const message = errBody?.message ?? httpError?.message ?? 'OTP login failed';
      const errCode = errBody?.code ?? errBody?.error ?? '';

      console.error('❌ OTP Login Error:', status, errCode, message);

      // Handle SESSION_EXPIRED specifically during login
      if (this.isAuthError(status, httpError?.statusText ?? '', message, errCode)) {
        console.warn('⚠️ Stale session detected during OTP login, clearing state...');
        await this.signOut(); // Clear cookies and storage
      }

      throw { message, status, code: errCode };
    }
  }


  async signOut(): Promise<void> {
    console.log("🚀 Initiating signOut...");
    await this.forceClearAll(); // Clear cookies and local/session storage first

    try {
      const token = this.jwt
        ?? this.session?.access_token
        ?? this.session?.jwt;

      const headers: any = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      await firstValueFrom(
        this.http.post(
          environment.apiUrl + '/api/auth/logout',
          {},
          { withCredentials: true, headers }
        )
      ).catch(() => { });

    } catch (e) {
      console.warn("Backend logout failed (ignoring)", e);
    }

    // ✅ THEN clear local state
    BackEndService.isHandlingAuthError = false;
    await this.setSession(null);
    this.jwt = null;
    this.session = null;

    const keysToClear = [
      BackEndService.SESSION_STORAGE_KEY,
      BackEndService.JWT_STORAGE_KEY,
      'loggedInEmployee',
      'empId',
      'invitee'
    ];
    await Promise.all(keysToClear.map(key => this.localDb.localDelete(key)));

    console.log("✅ Logout cleanup complete");
  }

  async rpcFunction(functionName: string, params: any): Promise<any> {
    const response: any = await this.apiCall(functionName, 'rpc', params);
    if (response?.error) {
      const message = response.error.message;
      if (
        response.status === 401 ||
        response.status === 403 ||
        message === 'JWT expired' ||
        (message && message.includes('row-level security policy'))
      ) {
        console.warn('Auth/Session error in rpcFunction, logging out...', message);
        const authService = this.injector.get(AuthService);
        authService.signOut(false, false, true); // silent auto-logout
      } else if (message && message !== 'The result contains 0 rows') {
        this.toast.error(message);
      }
    }
    return response;
  }

  async post(url: string, body: any): Promise<any> {
    const headers: any = {};
    const token = this.jwt ?? this.session?.access_token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      return await firstValueFrom(this.http.post(environment.apiUrl + url, body, { withCredentials: true, headers }));
    } catch (error: any) {
      this.toast.error(error?.error?.message || 'Request failed');
      throw error;
    }
  }

}